import { computed, getCurrentScope, onScopeDispose, ref, shallowRef } from 'vue'
import {
  ApiError,
  fetchCrashIssueDetail,
  fetchCrashIssues,
  fetchCrashStatsTrend,
  updateCrashIssueStatus,
  type CrashIssueApi,
  type CrashIssueDetailView,
  type CrashIssuePageView,
  type CrashIssueStatus,
  type CrashStatsTrendPeriod,
  type CrashStatsTrendView
} from '@xjlv2/api-client'

export type { CrashIssueApi } from '@xjlv2/api-client'
export type { CrashStatsTrendPeriod } from '@xjlv2/api-client'

/** 错误监控两视图：issue 列表 ↔ 单 issue 详情。 */
export type CrashIssuesViewMode = 'list' | 'detail'

export interface CrashIssueListRequest {
  page?: number
  size?: number
  /** null = 全部状态。 */
  status?: CrashIssueStatus | null
  keyword?: string | null
}

export interface CrashIssuesOptions {
  createApiClient: () => CrashIssueApi
  canView: () => boolean
}

/**
 * 错误监控取数：列表（分页/status/keyword）与详情（最新事件摘要）两视图切换；
 * requestSequence + AbortController 竞态防护照 useBehaviorAnalytics——旧请求迟到
 * 响应一律丢弃。状态流转（PUT）成功后以响应快照即时更新详情（后端返回更新后
 * issue 投影），并置 listStale：返回列表时重载，保证过滤/计数与最新状态一致；
 * 若 PUT 落地时用户已在列表视图（在途回返错过 stale 重载），立即重载当前筛选页。
 */
export function useCrashIssues(options: CrashIssuesOptions) {
  const view = ref<CrashIssuesViewMode>('list')
  const page = ref(1)
  const size = ref(20)
  const status = ref<CrashIssueStatus | null>(null)
  const keyword = ref('')

  // shallowRef：整包载荷按引用替换，避免深代理开销并保持载荷引用稳定。
  const list = shallowRef<CrashIssuePageView | null>(null)
  const detail = shallowRef<CrashIssueDetailView | null>(null)
  const detailFingerprint = ref('')

  // 趋势条（P2 Task 5）：与列表/详情并行的独立请求通道——不参与 list/detail 的
  // activeController 竞态（loadList 不得打断趋势、趋势也不得打断列表），进入列表页
  // 两者并行加载，period 切换仅重载趋势。
  const trendPeriod = ref<CrashStatsTrendPeriod>('LAST_7_DAYS')
  const trend = shallowRef<CrashStatsTrendView | null>(null)

  const listLoading = ref(false)
  const listError = ref('')
  const detailLoading = ref(false)
  const detailError = ref('')
  const trendLoading = ref(false)
  const trendError = ref('')
  const statusUpdating = ref(false)
  const statusError = ref('')
  const listStale = ref(false)
  const canView = computed(() => options.canView())

  let requestSequence = 0
  let activeController: AbortController | null = null
  // 每类视图各自的在途请求代次（T7 评审必修②）：loading 复位只认「自身类最新
  // 请求」——被跨类请求打断（如 openDetail 在途时 loadList 打断）的一方仍会复位
  // 自身 loading，不会搁浅；同类叠加（连续改筛选触发两次 loadList）时旧请求的
  // finally 不能把最新请求的 loading 提前清掉（否则详情/列表会闪「暂不可用」）。
  // activeController 仍按全局 sequence 守卫清理（仅最新请求有权释放）。
  let listRequestId = 0
  let detailRequestId = 0
  // 趋势独立代次 + 独立 controller（不与 list/detail 互斥）。
  let trendSequence = 0
  let trendController: AbortController | null = null

  async function loadList(request: CrashIssueListRequest = {}): Promise<void> {
    const nextPage = request.page ?? page.value
    const nextSize = request.size ?? size.value
    const nextStatus = request.status !== undefined ? request.status : status.value
    const nextKeyword = (request.keyword !== undefined ? request.keyword : keyword.value) ?? ''
    page.value = nextPage
    size.value = nextSize
    status.value = nextStatus
    keyword.value = nextKeyword

    if (!canView.value) {
      reset()
      return
    }

    const sequence = ++requestSequence
    const listRequest = ++listRequestId
    activeController?.abort()
    const controller = new AbortController()
    activeController = controller
    listLoading.value = true
    listError.value = ''

    try {
      const response = await fetchCrashIssues(apiWithSignal(controller), {
        page: nextPage,
        size: nextSize,
        status: nextStatus,
        keyword: nextKeyword
      })
      if (sequence !== requestSequence) return

      list.value = response
      listStale.value = false
    } catch (cause) {
      if (sequence !== requestSequence || isAbortError(cause)) return
      listError.value = describeCrashIssuesError(cause)
    } finally {
      if (listRequest === listRequestId) {
        listLoading.value = false
      }
      if (sequence === requestSequence) {
        activeController = null
      }
    }
  }

  async function loadDetail(fingerprint: string): Promise<void> {
    if (!canView.value) {
      reset()
      return
    }

    detailFingerprint.value = fingerprint
    const sequence = ++requestSequence
    const detailRequest = ++detailRequestId
    activeController?.abort()
    const controller = new AbortController()
    activeController = controller
    detailLoading.value = true
    detailError.value = ''

    try {
      const response = await fetchCrashIssueDetail(apiWithSignal(controller), fingerprint)
      if (sequence !== requestSequence) return

      detail.value = response
    } catch (cause) {
      if (sequence !== requestSequence || isAbortError(cause)) return
      detailError.value = describeCrashIssuesError(cause)
      detail.value = null
    } finally {
      if (detailRequest === detailRequestId) {
        detailLoading.value = false
      }
      if (sequence === requestSequence) {
        activeController = null
      }
    }
  }

  async function openDetail(fingerprint: string): Promise<void> {
    if (!fingerprint) return
    // 每次打开都重新取详情：监控视图保新鲜（最近事件/状态可能已变化）。
    view.value = 'detail'
    detail.value = null
    await loadDetail(fingerprint)
  }

  /**
   * 趋势加载（列表页顶部趋势条）：period 缺省 = 当前 trendPeriod。独立代次守卫
   * 迟到响应（快速切换 7/30 天时旧响应丢弃）；abort 信号透传但与列表/详情互不打断。
   */
  async function loadTrend(period?: CrashStatsTrendPeriod): Promise<void> {
    const nextPeriod = period ?? trendPeriod.value
    trendPeriod.value = nextPeriod
    if (!canView.value) {
      trend.value = null
      return
    }

    const sequence = ++trendSequence
    trendController?.abort()
    const controller = new AbortController()
    trendController = controller
    trendLoading.value = true
    trendError.value = ''

    try {
      const response = await fetchCrashStatsTrend(apiWithSignal(controller), {
        period: nextPeriod
      })
      if (sequence !== trendSequence) return

      trend.value = response
    } catch (cause) {
      if (sequence !== trendSequence || isAbortError(cause)) return
      trendError.value = describeCrashIssuesError(cause)
    } finally {
      if (sequence === trendSequence) {
        trendLoading.value = false
        trendController = null
      }
    }
  }

  async function backToList(): Promise<void> {
    view.value = 'list'
    if (listStale.value) {
      // 状态流转后列表口径已过期（状态过滤/计数）：返回即重载当前筛选页。
      await loadList()
    }
  }

  /**
   * 状态流转：PUT {status, assigneeId?}（assigneeId 缺省=保持现值）。成功后以
   * 响应的更新后 issue 快照即时替换详情头卡（即后端最新投影），并置 listStale
   * 在返回列表时重载；失败仅落在 statusError，不打断详情展示。
   */
  async function updateStatus(
    nextStatus: CrashIssueStatus,
    assigneeId?: number | null
  ): Promise<boolean> {
    const fingerprint = detailFingerprint.value
    if (!canView.value || !fingerprint) return false

    statusUpdating.value = true
    statusError.value = ''
    try {
      const client = options.createApiClient()
      const updated = await updateCrashIssueStatus(client, fingerprint, {
        status: nextStatus,
        assigneeId
      })
      const current = detail.value
      detail.value = current
        ? { issue: updated, recentEvents: current.recentEvents }
        : { issue: updated, recentEvents: [] }
      listStale.value = true
      if (view.value === 'list') {
        // PUT 在途期间用户已返回列表：返回时刻的 stale 重载已被错过（T7 评审
        // 必修③），流转落地即按当前筛选页重载，不留刷新空洞。
        void loadList()
      }
      return true
    } catch (cause) {
      statusError.value = describeCrashIssuesError(cause)
      return false
    } finally {
      statusUpdating.value = false
    }
  }

  function reset(): void {
    requestSequence += 1
    activeController?.abort()
    activeController = null
    trendSequence += 1
    trendController?.abort()
    trendController = null
    view.value = 'list'
    page.value = 1
    size.value = 20
    status.value = null
    keyword.value = ''
    list.value = null
    detail.value = null
    detailFingerprint.value = ''
    trendPeriod.value = 'LAST_7_DAYS'
    trend.value = null
    listLoading.value = false
    listError.value = ''
    detailLoading.value = false
    detailError.value = ''
    trendLoading.value = false
    trendError.value = ''
    statusUpdating.value = false
    statusError.value = ''
    listStale.value = false
  }

  /** 维度 fetch 契约只收 path；经此包装把 abort 信号透传到底层 ApiClient。 */
  function apiWithSignal(controller: AbortController): CrashIssueApi {
    const client = options.createApiClient()
    return {
      get: <T>(path: string, init?: RequestInit) => client.get<T>(path, { ...init, signal: controller.signal }),
      put: <T>(path: string, body?: unknown, init?: RequestInit) =>
        client.put<T>(path, body, { ...init, signal: controller.signal })
    }
  }

  if (getCurrentScope()) {
    onScopeDispose(reset)
  }

  return {
    view,
    page,
    size,
    status,
    keyword,
    list,
    detail,
    detailFingerprint,
    trendPeriod,
    trend,
    listLoading,
    listError,
    detailLoading,
    detailError,
    trendLoading,
    trendError,
    statusUpdating,
    statusError,
    canView,
    loadList,
    loadDetail,
    loadTrend,
    openDetail,
    backToList,
    updateStatus,
    reset
  }
}

/** 后端 CRASH_* 错误码 → 用户可读文案；未知错误透传 message。 */
export function describeCrashIssuesError(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.code === 'CRASH_ISSUE_PAGE_INVALID') return '分页参数不合法，请刷新后重试'
    if (cause.code === 'CRASH_ISSUE_STATUS_INVALID') return '状态值不合法，请重新选择'
    if (cause.code === 'CRASH_ISSUE_NOT_FOUND') return '该问题不存在或已被保留期清理'
    if (cause.code === 'CRASH_STATS_PERIOD_INVALID') return '统计周期不合法，请重新选择'
    return cause.message
  }
  return cause instanceof Error && cause.message ? cause.message : '错误监控数据加载失败'
}

function isAbortError(cause: unknown): boolean {
  return cause instanceof Error && cause.name === 'AbortError'
}
